import './applyPage.scss';

import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { FormEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import heroSearching from '../../../assets/hero-searching.svg';
import logo from '../../../assets/logo-default.svg';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useParameterStore from '../../../store/parameterStore/useParameterStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();
  const { next, resume } = useWorkflowStore();

  const {
    computed: { userFirstName },
  } = useUserStore();

  const {
    computed: { canResumeWorkflow },
  } = useApplyStore();

  useEffect(() => {
    // re-fetch parameters from the URL to support the case where the user was
    // navigated to this page from inside the app and we are using query params
    // to adjust the lead channel code for submission
    useParameterStore
      .getState()
      .setFromURLSearchParams(new URLSearchParams(window.location.search));
  }, []);

  const formSubmit = (event: FormEvent): void => {
    event.preventDefault();

    navigate(canResumeWorkflow ? resume() : next());
  };

  return (
    <div className="apply-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.apply.header.root}>
            <img alt={t('imageAlts.logo')} src={logo} />
            <button
              aria-label="Toggle drawer"
              data-testid={locator.pages.apply.header.drawerButton}
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Application completed percentage"
              data-testid={locator.pages.apply.progress.root}
              value={useWorkflowStore.getState().computed.progress}
              variant="determinate"
            />
          </div>

          <main>
            <figure>
              <img alt={t('imageAlts.heroSearching')} src={heroSearching} />
            </figure>
            <form
              data-testid={locator.pages.apply.form.root}
              noValidate
              onSubmit={formSubmit}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.apply.form.h1}>
                  {t('ApplyPage.title', { firstName: userFirstName })}
                </h1>
                <h2 data-testid={locator.pages.apply.form.h2}>
                  {t('ApplyPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <LoadingButton
                    color="primary"
                    data-testid={locator.pages.apply.form.submitButton}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t(
                      canResumeWorkflow
                        ? 'ApplyPage.resume'
                        : 'ApplyPage.continue',
                    )}
                  </LoadingButton>
                </div>
              </div>
              <footer data-testid={locator.pages.apply.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplyPage';
